/**
 * BİLDİRİMİN İŞARET ETTİĞİ EKRANIN ADRESİ — TEK TABLO.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Neden burada: iki kopya vardı ve biri sessizce yalan söylüyordu
 * ═══════════════════════════════════════════════════════════════════════════
 * Aynı eşleme iki yerde yaşıyordu:
 *
 *   • `apps/web/src/lib/notification-api.ts` — zil listesindeki satırın adresi
 *   • `packages/shared/src/mail.ts`          — e-postadaki düğmenin adresi
 *
 * İkisi ayrı ayrı bakımlandı. Panelde ayarlar `/dashboard/settings/*` altına
 * taşınınca web tablosu güncellendi, posta tablosu KALDI. Sonuç, 17 Eyl
 * 2026'da ölçüldü — dört varlığın postadaki düğmesi 404 bile dönmüyordu,
 * SONSUZ YÖNLENDİRMEYE giriyordu (`/dashboard/billing` → köprü → alan
 * segmenti eklenir → `/dashboard/w/<ws>/billing` → o da yok → köprü → …):
 *
 *   | varlık          | postadaki (yanlış)               | paneldeki (doğru)                |
 *   |-----------------|----------------------------------|----------------------------------|
 *   | invoice         | /dashboard/billing               | /dashboard/settings/billing      |
 *   | member          | /dashboard/team                  | /dashboard/settings/team         |
 *   | creative        | /dashboard/creatives/<id>        | /dashboard/creatives/history     |
 *   | workflow_action | /dashboard/automations/logs/<id> | /dashboard/automations/approvals |
 *
 * (O günün tablosu. 5 Eki 2026'dan beri onay kuyruğu `/dashboard/agent/approvals`.)
 *
 * Ayrıca `account` ve `vision_analysis` posta tablosunda HİÇ yoktu: güvenlik
 * postası ("parolan değişti") düğmesiz gidiyordu.
 *
 * Tablo artık tek. Bir rota değiştiğinde iki dosyayı birden hatırlamak
 * gerekmiyor — zaten hatırlanmadığı için bu hata doğdu.
 *
 * ---------------------------------------------------------------------------
 * Neden kök `index.ts` barrel'ında (`mail.ts`in aksine)
 * ---------------------------------------------------------------------------
 * `mail.ts` alt yolda tutuluyor çünkü bir dönem posta HTML'i taşıyordu ve
 * tarayıcı bundle'ına girmesi istenmiyordu. Buradaki şey ise birkaç satırlık
 * saf bir dize eşlemesi ve arayüzün ZATEN ihtiyaç duyduğu veri — zil
 * listesindeki her satırın adresi buradan çıkıyor. Alt yola koymak, web'in
 * onu kullanmasını zorlaştırmaktan başka bir şey yapmazdı.
 *
 * ---------------------------------------------------------------------------
 * Yol ALAN SEGMENTİ TAŞIMIYOR
 * ---------------------------------------------------------------------------
 * Üretilen yol `/dashboard/…` biçiminde; etkin çalışma alanı (`/dashboard/w/
 * <slug>/…`) eklenmiyor ve eklenemez: bildirim gönderilirken alıcının o an
 * hangi alanı açacağı bilinmiyor, üstelik bir kullanıcı birden çok alanın
 * üyesi olabiliyor. Segmenti web tarafında `withWorkspace()` (panel içi
 * gezinme), postada ise `/dashboard/[...rest]` köprüsü ekliyor — köprü
 * kullanıcının GİRİŞ alanını çözüp oraya yolluyor.
 */

import { toLocale, type Locale } from './locales.js';

/**
 * Bildirimin `data.ref` varlığı → panel içi yol.
 *
 * Tanınmayan varlık için tahmin edilmiş bir adres üretilmiyor: 404'e giden
 * bir düğme, düğmesizlikten kötü. Bu yüzden `notificationPath` `null`
 * dönebiliyor ve iki çağıran da bunu "bağlantısız satır" olarak işliyor.
 *
 * 🔴 Buraya bir satır eklemeden önce rotanın GERÇEKTEN var olduğunu doğrula:
 * `apps/web/src/app/[locale]/dashboard/w/[ws]/(shell)/…` altında karşılığı
 * olmayan bir yol, köprü yüzünden 404 değil sonsuz yönlendirme üretir.
 */
const ENTITY_ROUTE: Record<string, (id: string) => string> = {
  campaign: (id) => `/dashboard/campaigns/${id}`,
  /* 5 Eki 2026 — kural ekranları kalktı (kullanıcı kararı: kuralı kimse elle
     kurmuyor, AI devriyede). Kuralın da aksiyonun da kendi sayfası yok; ikisi
     de onay kuyruğuna gidiyor — kuralın ürettiği iş orada karar bekliyor. */
  workflow: () => '/dashboard/agent/approvals',
  workflow_action: () => '/dashboard/agent/approvals',
  creative: () => '/dashboard/creatives/history',
  /* Ürün görseli analizi KÜTÜPHANEYE değil STÜDYOYA gidiyor: analizin çıktısı
     üretilmiş bir kreatif değil, seçilmeyi bekleyen üç konsept. Kimlik
     kullanılmıyor çünkü stüdyo hangi analizin açık olduğunu kendi kaynağından
     (`assetKey`/`productId`) çözüyor — analiz kimliğiyle açılabilen bir ekran
     yok. */
  vision_analysis: () => '/dashboard/creatives',
  /* Prodüksiyon (reklam videosu). KİMLİKLİ — prodüksiyon sayfası
     (`creatives/productions/[id]/page.tsx`, 1 Eki 2026). Faz 7 arayüzü o
     dosyanın yerine gerçek sayfayı yazana kadar sayfa tezgâha yönlendiriyor;
     adres değişmeyecek, yani bugün gönderilen bildirim yarın da doğru yere
     açılır. `notification-route.spec.ts` dosya sisteminden doğruluyor. */
  production: (id) => `/dashboard/creatives/productions/${id}`,
  connection: () => '/dashboard/connections',
  invoice: () => '/dashboard/settings/billing',
  member: () => '/dashboard/settings/team',
  /* 🔴 17 Eyl 2026 — KİMLİKLİ hâle geldi.
     Bu satır bir gün kimliksizdi ve gerekçesi doğruydu: `/dashboard/support/
     <id>` altında bir sayfa YOKTU ve var olmayan bir yola bağlanmak, köprü
     yüzünden bu tablodaki en pahalı hatayı (sonsuz yönlendirme) üretiyor.
     Sayfa yazıldı (`support/[id]/page.tsx`), yani bildirime tıklayan kullanıcı
     artık listeyi tarayıp kendi biletini aramak yerine doğrudan yazışmayı
     açıyor. */
  support_ticket: (id) => `/dashboard/support/${id}`,
  /* Güvenlik bildirimleri (parola değişti, yeni cihazdan giriş) kimlik
     taşımıyor: kullanıcının yapması gereken şey bir kaydı açmak değil,
     oturumlarına bakıp gerekiyorsa parolasını değiştirmek. */
  account: () => '/dashboard/settings/security',
  /* Otonom operatörün koşusu (29 Eyl 2026 — `agent.operator_applied`,
     `agent.operator_insight`). Koşunun kendi sayfası yok; Operatör ekranı
     koşu listesini ve ayrıntısını açıyor. Onay kuyruğuna GİTMİYOR: orada
     yalnız BEKLEYEN satırlar var, uygulanmış değişiklik görünmez. */
  operator_run: (id) => (id ? `/dashboard/agent/operator?run=${encodeURIComponent(id)}` : '/dashboard/agent/operator'),
  /* AI sohbeti (1 Eki 2026 — takip görevinin sonucu kaynak sohbete yazılıyor,
     `agent.operator_insight` oraya götürüyor). Sohbet sayfası `?c=`yi
     doğrudan açılışta okuyor (`agent/page.tsx`). */
  agent_conversation: (id) => (id ? `/dashboard/agent?c=${encodeURIComponent(id)}` : '/dashboard/agent'),
  /* Abonelik hatırlatması (1 Eki 2026 — `billing.subscription_reminder`,
     `payment_overdue`, `grace_ending`, `renewal_requested`). Ödeme sayfası
     plan seçici AÇIK: bildirimin tek işi "aboneliği başlat". Alıcılar sahip
     ve yöneticiler — ikisinde de `billing.read` var, sayfa açılıyor. */
  subscription: () => '/dashboard/settings/billing?plans=open',
};

/**
 * `{ entity, id }` → panel içi yol (alan segmenti YOK, dil öneki YOK).
 *
 * Gevşek imza bilinçli: çağıranlardan biri veritabanından okunan `Json`
 * sütunuyla (`data`) geliyor ve orada `entity` bir dize olmayabilir.
 */
export function notificationPath(
  ref: { entity?: unknown; id?: unknown } | null | undefined,
): string | null {
  if (!ref) return null;

  const entity = typeof ref.entity === 'string' ? ref.entity : null;
  if (!entity) return null;

  const build = ENTITY_ROUTE[entity];
  if (!build) return null;

  return build(typeof ref.id === 'string' ? ref.id : '');
}

/// Tanınan varlıklar — test ve denetim için okunabilir liste.
export const NOTIFICATION_ROUTE_ENTITIES = Object.keys(ENTITY_ROUTE);

/**
 * HAM `User.locale` → adres çubuğundaki dil öneki.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden bir dönüşüm gerekiyor — `locale as Locale` neden yalandı
 * ═══════════════════════════════════════════════════════════════════════════
 * İki ayrı "dil" var ve aynı değiller:
 *
 *   • POSTANIN dili — `NotificationLocaleService` çözüyor. Yönetici panelden
 *     dil ekleyebiliyor, yani `tr`/`en` dışında bir kod ("de") olabilir.
 *   • BAĞLANTININ dil öneki — web uygulamasının rota dili. `routing.locales`
 *     `LOCALES` listesi (`locales.ts`) ve listede olmayan bir önek 404 döner.
 *
 * Üç çağrı yeri (`verify-email`, `auth/link` — 10 Eki 2026'ya kadar
 * `reset-password` —, `email-change`) bu ikisini
 * karıştırıyordu: ikisi ham değeri `as Locale` ile susturuyor, biri elle
 * `=== 'tr' ? …` yazıyordu. Üçü de aynı şeyi yapmalı ve tek yerde.
 *
 * 29 Eyl 2026: iki dilden on üç dile çıkıldı; indirgeme artık `toLocale`
 * (`pt-BR` → `pt`, bilinmeyen → `en`).
 */
export function routeLocale(locale: string | null | undefined): Locale {
  return toLocale(locale);
}
